import { useEffect, useMemo } from "react";
import L from "leaflet";
import { Circle, MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";

const PIN = L.divIcon({
  className: "",
  html: '<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:#ff1d00;border:2px solid #fff;transform:rotate(-45deg);box-shadow:0 0 14px rgba(255,29,0,.85),0 2px 6px rgba(0,0,0,.6)"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

export const FEET_TO_METERS = 0.3048;

interface Props {
  lat: number | null;
  lng: number | null;
  radiusFeet: number | null;
  onChange: (lat: number, lng: number) => void;
}

function ClickToPlace({ onChange }: { onChange: Props["onChange"] }) {
  useMapEvents({ click: (e) => onChange(e.latlng.lat, e.latlng.lng) });
  return null;
}

function Recenter({ lat, lng }: { lat: number | null; lng: number | null }) {
  const map = useMap();
  useEffect(() => {
    if (lat !== null && lng !== null) map.setView([lat, lng], Math.max(map.getZoom(), 15));
  }, [lat, lng, map]);
  return null;
}

export function VenueMap({ lat, lng, radiusFeet, onChange }: Props) {
  const has = lat !== null && lng !== null;
  const handlers = useMemo(() => ({
    dragend: (e: L.LeafletEvent) => {
      const p = (e.target as L.Marker).getLatLng();
      onChange(p.lat, p.lng);
    },
  }), [onChange]);
  return (
    <div data-testid="venue-map" className="h-[380px] overflow-hidden rounded-sm border border-white/10">
      <MapContainer center={has ? [lat, lng] : [39.5, -98.35]} zoom={has ? 15 : 4} className="h-full w-full" scrollWheelZoom>
        <TileLayer className="map-tiles-dark" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
        <ClickToPlace onChange={onChange} />
        <Recenter lat={lat} lng={lng} />
        {has && (
          <>
            <Marker position={[lat, lng]} draggable icon={PIN} eventHandlers={handlers} />
            {radiusFeet !== null && radiusFeet > 0 && (
              <Circle center={[lat, lng]} radius={radiusFeet * FEET_TO_METERS} pathOptions={{ color: "#ff1d00", weight: 2, fillOpacity: 0.12 }} />
            )}
          </>
        )}
      </MapContainer>
    </div>
  );
}
